"use client";

import Link from "@/components/ui/CcLink";

import { CcMegaAllLink } from "@/components/ui/CcMegaAllLink";
import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { CcNavInlineDetailLabel } from "@/components/layout/CcNavInlineDetailLabel";
import type {
  NavServiceMegaColumn,
  NavServicesMegaCopy,
} from "@/lib/cc-nav-services-mega";
import { shouldUseCompactServicesNav } from "@/lib/cc-nav-publish";
import {
  useCcNavMegaMountEl,
  useCcNavMegaPanelPosition,
} from "@/lib/cc-nav-mega-panel-position";
import type { NavServiceItem } from "@/lib/services";

const OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 240;
const HOVER_INTENT_MQ =
  "(hover: hover) and (pointer: fine) and (min-width: 811px)";

type CcNavServicesMenuProps = {
  label: string;
  href: string;
  isActive: boolean;
  serviceItems: NavServiceItem[];
  serviceMegaColumns: NavServiceMegaColumn[];
  megaCopy: NavServicesMegaCopy;
  onNavigate: () => void;
  /** When on a service detail page, shown as "Label / Detail" in the trigger */
  detailTitle?: string | null;
  /** Hub segment before detail (e.g. service hub on a sub-page). */
  detailLead?: string | null;
  detailLeadHref?: string | null;
  detailTitleFallback?: string | null;
  detailInitiallySettled?: boolean;
  onDetailRailActiveChange?: (active: boolean) => void;
  /** Desktop mega panels mount here (outside `.cc-nav__links` scroll/clip). */
  megaMountRef?: RefObject<HTMLElement | null>;
};

export function CcNavServicesMenu({
  label,
  href,
  isActive,
  serviceItems,
  serviceMegaColumns,
  megaCopy,
  onNavigate,
  detailTitle = null,
  detailLead = null,
  detailLeadHref = null,
  detailTitleFallback = null,
  detailInitiallySettled = false,
  onDetailRailActiveChange,
  megaMountRef,
}: CcNavServicesMenuProps) {
  const compactNav = shouldUseCompactServicesNav(serviceMegaColumns);
  const [detailRailActive, setDetailRailActive] = useState(false);
  const showDetailLayout =
    Boolean(detailTitle) ||
    Boolean(detailTitleFallback) ||
    detailRailActive;
  const detailAria = detailTitle ?? detailTitleFallback ?? null;
  const hasSegmentedTrail =
    Boolean(detailLead?.trim()) &&
    Boolean(detailLeadHref?.trim()) &&
    Boolean(detailAria?.trim());

  const handleDetailRailChange = useCallback(
    (active: boolean) => {
      setDetailRailActive(active);
      onDetailRailActiveChange?.(active);
    },
    [onDetailRailActiveChange],
  );
  const pathname = usePathname();
  const activeDetailIndex = serviceItems.findIndex(
    (item) =>
      pathname === item.href || pathname.startsWith(`${item.href}/`) ||
      serviceMegaColumns.find(column => column.headerHref === item.href)?.links.some(link => pathname === link.href),
  );
  const activeSubpageIndex = serviceMegaColumns
    .find((column) => column.headerHref === detailLeadHref)
    ?.links.findIndex(
      (item) => pathname === item.href || pathname.startsWith(`${item.href}/`),
    ) ?? -1;
  const breadcrumbIndex = hasSegmentedTrail ? activeSubpageIndex : activeDetailIndex;
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerSpanRef = useRef<HTMLSpanElement>(null);
  const focusTrigger = () => {
    triggerSpanRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
  };
  const panelRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const focusFirstRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [hoverIntent, setHoverIntent] = useState(false);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  useLayoutEffect(() => {
    const mq = window.matchMedia(HOVER_INTENT_MQ);
    const sync = () => setHoverIntent(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    closeNow();
  }, [pathname, closeNow]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!open || !focusFirstRef.current) return;
    focusFirstRef.current = false;
    firstItemRef.current?.focus();
  }, [open]);

  const megaMountEl = useCcNavMegaMountEl(megaMountRef);
  const pillMounted = Boolean(megaMountEl);

  useCcNavMegaPanelPosition(
    open,
    megaMountEl,
    triggerSpanRef,
    panelRef,
    [compactNav, serviceItems, serviceMegaColumns],
  );

  const scheduleOpen = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open) return;
    if (openTimerRef.current !== null) return;
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (!open) return;
    if (closeTimerRef.current !== null) return;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const handlePointerEnter = () => {
    if (!hoverIntent) return;
    scheduleOpen();
  };

  const handlePointerLeave = () => {
    if (!hoverIntent) return;
    scheduleClose();
  };

  const handleTriggerClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      hoverIntent || event.metaKey || event.ctrlKey || event.shiftKey ||
      event.altKey || event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    setOpen((current) => !current);
  };

  const handleTriggerKeyDown = (
    event: KeyboardEvent<HTMLAnchorElement | HTMLSpanElement>,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (open) {
        firstItemRef.current?.focus();
        return;
      }
      focusFirstRef.current = true;
      openNow();
    }

    if (event.key === "Escape" && open) {
      event.preventDefault();
      closeNow();
      focusTrigger();
    }
  };

  const handleRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    closeNow();
    focusTrigger();
  };

  const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && rootRef.current?.contains(next)) return;
    if (next instanceof Node && panelRef.current?.contains(next)) return;
    closeNow();
  };

  const handlePanelPointerEnter = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleOpen();
  };

  const handlePanelPointerLeave = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleClose();
  };

  const handleItemNavigate = () => {
    closeNow();
    onNavigate();
  };

  let firstLinkAssigned = false;

  const panel = (
      <div
        ref={panelRef}
        id={panelId}
        className={`cc-nav-mega cc-nav-services-panel ${compactNav ? "cc-nav-services-panel--compact" : ""} ${pillMounted ? "cc-nav-mega--pill-mounted" : ""} ${open ? "cc-nav-mega--open" : ""}`}
        inert={!open}
        aria-hidden={!open}
        onPointerEnter={handlePanelPointerEnter}
        onPointerLeave={handlePanelPointerLeave}
      >
        <div className="cc-nav-mega__chrome" data-cc-cursor-ignore aria-hidden="true" />
        {compactNav ? (
          <ul className="cc-nav-services-panel__list cc-nav-services-panel__list--compact">
            {serviceItems.map((item, index) => {
              const itemActive =
                pathname === item.href || pathname.startsWith(`${item.href}/`);
              const assignFirstRef = index === 0;
              return (
                <li key={item.href} className="cc-nav-mega__row">
                  <Link
                    ref={assignFirstRef ? firstItemRef : undefined}
                    href={item.href}
                    className={`cc-nav-mega__item ${itemActive ? "cc-nav-mega__item--active" : ""}`}
                    onNavigate={handleItemNavigate}
                  >
                    <span className="cc-nav-mega__item-label">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
        <div className="cc-nav-services-panel__grid">
          {serviceMegaColumns.map((column) => (
            <section
              key={column.tag}
              className="cc-nav-services-panel__column"
              aria-labelledby={`${panelId}-${column.tag}-head`}
            >
              <CcMegaAllLink
                id={`${panelId}-${column.tag}-head`}
                href={column.headerHref}
                className="cc-nav-services-panel__column-head"
                onNavigate={handleItemNavigate}
              >
                {column.header}
              </CcMegaAllLink>
              <ul className="cc-nav-services-panel__list">
                {column.links.map((link) => {
                  const itemActive =
                    pathname === link.href ||
                    pathname.startsWith(`${link.href}/`);
                  const assignFirstRef = !firstLinkAssigned;
                  if (assignFirstRef) {
                    firstLinkAssigned = true;
                  }
                  return (
                    <li key={link.key} className="cc-nav-mega__row">
                      <Link
                        ref={assignFirstRef ? firstItemRef : undefined}
                        href={link.href}
                        className={`cc-nav-mega__item ${itemActive ? "cc-nav-mega__item--active" : ""}`}
                        onNavigate={handleItemNavigate}
                      >
                        <span className="cc-nav-mega__item-label">{link.label}</span>
                        <span className="cc-nav-mega__item-desc">
                          {link.description}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        )}
        <div className="cc-nav-mega__rule" data-cc-cursor-ignore />
        <CcMegaAllLink href={href} onNavigate={handleItemNavigate}>
          {megaCopy.seeAll}
        </CcMegaAllLink>
      </div>
  );

  return (
    <div
      ref={rootRef}
      className={`cc-nav-services ${open ? "cc-nav-services--open" : ""}`}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleRootKeyDown}
      onBlur={handleRootBlur}
    >
      {/* Keep the animation component mounted as the trail gains/loses segments. */}
      <span
        ref={triggerSpanRef}
        className={`cc-nav__link cc-nav-services__trigger ${isActive ? "cc-nav__link--active" : ""} ${showDetailLayout ? "cc-nav__link--has-detail" : ""} ${hasSegmentedTrail ? "cc-nav-services__trigger--segmented" : ""}`}
        onKeyDown={handleTriggerKeyDown}
      >
        <CcNavInlineDetailLabel
          label={label}
          labelHref={href}
          labelMenu={{ expanded: open, controls: panelId }}
          onLabelClick={hasSegmentedTrail ? undefined : handleTriggerClick}
          detail={detailTitle}
          detailLead={detailLead}
          detailLeadHref={detailLeadHref}
          onSegmentNavigate={onNavigate}
          detailIndex={breadcrumbIndex >= 0 ? breadcrumbIndex : null}
          detailLeadIndex={activeDetailIndex >= 0 ? activeDetailIndex : null}
          detailFallback={detailTitleFallback}
          detailInitiallySettled={detailInitiallySettled}
          onDetailRailActiveChange={handleDetailRailChange}
        />
      </span>

      {open ? (
        <span className="cc-nav-services__bridge" aria-hidden="true" />
      ) : null}

      {pillMounted && megaMountEl
        ? createPortal(panel, megaMountEl)
        : panel}
    </div>
  );
}
